import { useState } from 'react';

import { useForm } from 'react-hook-form';
import { useNavigate } from 'react-router-dom';

import { enqueueSnackbar } from 'notistack';

import {
  LS_IS_JUST_SIGNED_IN_NAME,
  LS_IS_JUST_SIGNED_IN_VALUE,
  useCognitoCheckUser,
} from '@/entities/user/currentUser';
import { setAuthForm, setViewerEmail, setViewerPassword } from '@/entities/viewer';

import { AUTH_API } from '@/shared/api/amplify';
import { PATHS } from '@/shared/constants/routes/paths';
import { IS_WHITE_LABEL } from '@/shared/environments';
import { showErrorAlert } from '@/shared/lib/showErrorAlert';
import { AlertType } from '@/shared/types/Alert';
import { AuthForm, CognitoErrors } from '@/shared/types/Cognito';
import { AuthError } from '@/shared/types/Error';
import { PasswordInput } from '@/shared/ui/form/PasswordInput';

import { Divider } from '../../components/Divider/styled';
import { EmailInput } from '../../components/EmailInput';
import { FormContainer } from '../../components/FormContainer';
import { GoogleSigninButton } from '../../components/GoogleSigninButton';
import { SubmitButton } from '../../components/SubmitButton/styled';
import { TermsOfService } from '../../components/TermsOfService';
import { DATA_TEST_IDS } from '../../lib/constants';
import { Block, Container, DividerContainer, Link, LinkContainer, Subtitle, TextFieldsContainer } from './styled';

type FormValues = {
  email: string;
  password: string;
};

export const Signin = () => {
  const navigate = useNavigate();
  const [isLoading, setIsLoading] = useState(false);

  const { refetch: refetchCognitoUser } = useCognitoCheckUser({ enabled: false });

  const { control, handleSubmit, reset } = useForm<FormValues>({
    defaultValues: {
      email: '',
      password: '',
    },
    mode: 'onSubmit',
  });

  const onSubmit = async (input: FormValues) => {
    const { email, password } = input;
    try {
      setIsLoading(true);

      setViewerEmail(email);
      setViewerPassword(window.btoa(password));

      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const response = await AUTH_API.signIn(email, password);

      if (response) {
        await refetchCognitoUser();
        localStorage.setItem(LS_IS_JUST_SIGNED_IN_NAME, LS_IS_JUST_SIGNED_IN_VALUE);
        navigate(PATHS.homepage);
        reset();
      }
    } catch (e) {
      const error = e as AuthError;

      if (error.code === CognitoErrors.UserNotConfirmed) {
        await AUTH_API.resendConfirmationCode(email);

        enqueueSnackbar('Confirmation code was sent to your email', {
          variant: AlertType.Success,
        });

        setAuthForm(AuthForm.ConfirmEmail);
        return;
      }

      if (error.code === CognitoErrors.PasswordResetRequired) {
        await AUTH_API.forgotPassword(email);

        enqueueSnackbar('Please update your password. We sent confirmation code to your email', {
          variant: AlertType.Success,
        });

        setAuthForm(AuthForm.ChangePassword);
        return;
      }

      showErrorAlert(e);
    } finally {
      setIsLoading(false);
    }
  };

  const switchToSigninForm = () => {
    setAuthForm(AuthForm.Signup);
  };

  const switchToForgotPasswordForm = () => {
    setAuthForm(AuthForm.ForgotPassword);
  };

  return (
    <>
      <FormContainer title="Sign in" subtitle="Continue translating video 🤘" onSubmit={handleSubmit(onSubmit)}>
        <Container>
          {!IS_WHITE_LABEL && (
            <>
              <GoogleSigninButton />

              <DividerContainer>
                <Divider>
                  <Subtitle>Or Sign Up with e-mail</Subtitle>
                </Divider>
              </DividerContainer>
            </>
          )}

          <TextFieldsContainer>
            <EmailInput control={control} isDisabled={isLoading} />
            <PasswordInput control={control} isDisabled={isLoading} placeholder="Password" />
            <LinkContainer>
              <Link data-testid={DATA_TEST_IDS.forgotPasswordButton} onClick={switchToForgotPasswordForm}>
                <span>Forgot password?</span>
              </Link>
            </LinkContainer>
          </TextFieldsContainer>

          <SubmitButton
            data-testid={DATA_TEST_IDS.signInButton}
            disabled={isLoading}
            isLoading={isLoading}
            onClick={handleSubmit(onSubmit)}
          >
            Sign in
          </SubmitButton>
          {!IS_WHITE_LABEL && (
            <Block>
              <Subtitle>Don't have an account?</Subtitle>
              <Link data-testid={DATA_TEST_IDS.signUpButton} onClick={switchToSigninForm}>
                <span>Sign up</span>
              </Link>
            </Block>
          )}
        </Container>
      </FormContainer>
      {!IS_WHITE_LABEL && <TermsOfService />}
    </>
  );
};
